import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Compass, Sparkles, GraduationCap, Briefcase, IndianRupee, Loader2, Settings2, BriefcaseBusiness } from "lucide-react";
import { BrandWordmark } from "@/components/Brand";
import api from "@/lib/api";
import { toast } from "sonner";

const SIDEBAR = [
    { to: "/admin", label: "Overview", testid: "admin-nav-overview" },
    { to: "/admin/clients", label: "Brands", icon: Compass, testid: "admin-nav-clients" },
    { to: "/admin/doctor", label: "Dr. Aangan", icon: Sparkles, testid: "admin-nav-doctor" },
    { to: "/admin/academy", label: "Academy", icon: GraduationCap, testid: "admin-nav-academy" },
    { to: "/admin/careers", label: "Careers", icon: Briefcase, testid: "admin-nav-careers" },
    { to: "/admin/finance", label: "Finance", icon: IndianRupee, testid: "admin-nav-finance" },
    { to: "/admin/consultations", label: "Pricing", icon: Settings2, testid: "admin-nav-consultations" },
    { to: "/admin/jobs", label: "Job Postings", icon: BriefcaseBusiness, testid: "admin-nav-jobs" },
];

export default function AdminShell({ children, title }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(location.state?.user || null);
    const [loading, setLoading] = useState(!location.state?.user);

    useEffect(() => {
        if (location.state?.user) return;
        api
            .get("/auth/me")
            .then((r) => {
                if (!r.data?.is_admin) {
                    navigate("/admin/login", { replace: true });
                } else {
                    setUser(r.data);
                }
            })
            .catch(() => navigate("/admin/login", { replace: true }))
            .finally(() => setLoading(false));
    }, [location.state, navigate]);

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            console.warn("logout failed", e);
        }
        toast.success("Signed out");
        navigate("/admin/login", { replace: true });
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-black">
                <Loader2 className="animate-spin text-saffron" size={28} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white grid grid-cols-1 lg:grid-cols-[240px_1fr]" data-testid="admin-shell">
            <aside className="border-r border-white/8 p-6 lg:sticky lg:top-0 lg:h-screen flex flex-col">
                <Link to="/" className="flex items-center mb-10">
                    <BrandWordmark height={22} />
                </Link>
                <nav className="space-y-1 flex-1">
                    {SIDEBAR.map((s) => {
                        const active = location.pathname === s.to;
                        const Icon = s.icon;
                        return (
                            <Link
                                key={s.to}
                                to={s.to}
                                data-testid={s.testid}
                                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                                    active
                                        ? "text-saffron border-l-2 border-saffron bg-saffron/5"
                                        : "text-white/65 hover:text-white border-l-2 border-transparent"
                                }`}
                            >
                                {Icon && <Icon size={14} strokeWidth={1.5} />}
                                {s.label}
                            </Link>
                        );
                    })}
                </nav>
                <div className="border-t border-white/8 pt-4 mt-4">
                    {user && (
                        <>
                            <div className="text-xs text-white/55 truncate" data-testid="admin-user-email">{user.email}</div>
                            <button onClick={logout} className="flex items-center gap-2 text-xs text-white/55 hover:text-white mt-3" data-testid="admin-logout">
                                <LogOut size={12} />
                                Sign out
                            </button>
                        </>
                    )}
                </div>
            </aside>

            <main className="p-6 lg:p-10">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="font-display text-3xl lg:text-4xl tracking-tighter">{title}</h1>
                </div>
                {children}
            </main>
        </div>
    );
}
